// Attribution on the way IN to the game (TAS-371).
//
// Most of TAS-371 is about links leaving the game. This is the other
// direction: a player who arrives on a tokenised link — today the recap's
// sign-in nudge, tomorrow anything we send — and then creates an account.
// Without this, "Anonymous Player Converted" says a guest signed up and
// nothing about what made them.
//
// Deliberately thin. The game is one tab for one evening, so the tab's own
// store is enough; there is no checkout redirect to survive here and no cookie
// worth setting for it. Reading is lazy and memoised, and every access is
// wrapped — a private window that throws on storage must not cost anyone their
// account.

import { readVisitAttribution, attributionEventProps } from '@tasted/utils/attribution';

const KEY = 'tasted_inbound_attribution';
// Same DEV/prod split as playerStorage: sessionStorage keeps test windows
// isolated, localStorage survives a refresh on a player's own phone.
const store = () => (import.meta.env.DEV ? sessionStorage : localStorage);

let _cached;
let _read = false;

/** What brought this player to the game, or null. */
export function getInboundAttribution() {
  if (_read) return _cached;
  _read = true;
  _cached = null;
  if (typeof window === 'undefined') return null;

  try {
    // readVisitAttribution reads the token analytics-logger already took off
    // the address bar at module load, then the query string. Both matter:
    // /join/CODE?t=… redirects to /onboarding within a tick, so by the time a
    // player signs up the URL is long gone.
    const fromUrl = readVisitAttribution();
    if (fromUrl) {
      try {
        store().setItem(KEY, JSON.stringify(fromUrl));
      } catch {
        /* private mode — the in-memory copy still covers this load */
      }
      _cached = fromUrl;
      return _cached;
    }
    const raw = store().getItem(KEY);
    const stored = raw ? JSON.parse(raw) : null;
    _cached = stored?.src ? stored : null;
  } catch {
    _cached = null;
  }
  return _cached;
}

/**
 * src / campaign / session_id / player_id for an event, or {} — so it can be
 * spread into any track() call without a guard. No PII (TAS-73).
 */
export function inboundAttributionProps() {
  return attributionEventProps(getInboundAttribution());
}

/**
 * Persist whatever brought this player here, at startup.
 *
 * Called from main.jsx BEFORE the router mounts. Capturing lazily at the
 * conversion event was too late: `/join/CODE?t=…` navigates to /onboarding
 * immediately, and a reload anywhere after that loses a token that was only
 * ever held in memory. getInboundAttribution() writes on read, so this is the
 * whole of it.
 */
export function captureInboundAttribution() {
  return getInboundAttribution();
}

/** Test seam: forget what was read so the next call re-reads the URL. */
export function __resetInboundAttributionCache() {
  _read = false;
  _cached = undefined;
}
